import { useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

/**
 * Reusable confirm dialog controller. Renders a "Sei sicuro?" modal.
 *
 * Usage:
 *   const { confirm, dialog } = useConfirmDialog();
 *   const ok = await confirm({ title:'Elimina', description:'…' });
 *   if (ok) { ... }
 *   // in JSX: {dialog}
 */
export function useConfirmDialog() {
  const [state, setState] = useState({ open: false, title: "", description: "", confirmLabel: "Elimina", destructive: true });
  const [resolver, setResolver] = useState(null);

  const confirm = ({ title = "Sei sicuro?", description = "", confirmLabel = "Elimina", destructive = true } = {}) => {
    setState({ open: true, title, description, confirmLabel, destructive });
    return new Promise((resolve) => setResolver(() => resolve));
  };

  const close = (result) => {
    setState((s) => ({ ...s, open: false }));
    if (resolver) { resolver(result); setResolver(null); }
  };

  const dialog = (
    <AlertDialog open={state.open} onOpenChange={(v) => !v && close(false)}>
      <AlertDialogContent data-testid="confirm-dialog" className="bg-[color:var(--bg-primary)]">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif text-2xl">{state.title}</AlertDialogTitle>
          {state.description && (
            <AlertDialogDescription className="text-sm text-[color:var(--text-secondary)]">
              {state.description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel" onClick={() => close(false)} className="rounded-full">
            Annulla
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-accept"
            onClick={() => close(true)}
            className={`rounded-full ${state.destructive ? "bg-red-700 hover:bg-red-800 text-white" : "bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] text-white"}`}
          >
            {state.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, dialog };
}
